import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as motion from "motion/react-client";
import { commonStyle } from "../common.styles";
import theme, { responsiveFontSize } from "themes/Theme";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { AccountActivateRequest } from "reduxStore/dataTypes";
import {
  activateAccountRequest,
  requestActivationEmail,
} from "services/apiService";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Logo } from "assets/index";
import { saveToStore, StoreType } from "services/localStorage";
import { authSuccess } from "reduxStore/reducers/authReducer";

const VerificationCodePage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [otp, setOtp] = useState<string[]>(new Array(4).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [counter, setCounter] = useState(60);
  const [isDisabled, setIsDisabled] = useState(true);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return; // Allow only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to the next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    try {
      if (state?.userId) {
        setLoading(true);
        const body: AccountActivateRequest = {
          userId: state.userId,
          otp: otp.join(""),
        };
        const response = await activateAccountRequest(body);
        if (response && response.status === 200) {
          toast.success(response?.data?.message);
          dispatch({ type: "RESET_ALL_STATE" });
          saveToStore(response.data.data, StoreType.AUTH);
          saveToStore(response.data.data.token, StoreType.AUTH_TOKEN);
          dispatch(authSuccess(response.data.data));
          navigate("/dashboard");
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isDisabled) {
      timer = setInterval(() => {
        setCounter((prev) => {
          if (prev === 1) {
            clearInterval(timer);
            setIsDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [isDisabled]);

  const handleResendOTP = async () => {
    setCounter(60);
    setIsDisabled(true);
    try {
      if (state.email) {
        setLoading(true);
        const response = await requestActivationEmail(state.email);
        if (response && response.status === 200) {
          toast.success(response?.data?.message);
        } else {
          toast.error(response?.data?.message);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetDispatch = () => {
    dispatch({ type: "RESET_ALL_STATE" });
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      transition={{
        type: "spring",
        stiffness: 50,
      }}
    >
      <Box>
        <center>
          <Box
            component={"img"}
            src={Logo}
            sx={{
              width: "120px",
              height: "120px",
              objectFit: "contain",
              "&:hover": { cursor: "pointer" },
            }}
            onClick={() => {
              navigate("/");
            }}
          />
        </center>
        <Typography style={commonStyle.titleText}>
          Verify your email address
        </Typography>
        <Typography sx={styles.subText}>
          Check your inbox to verify your email and activate the account
        </Typography>

        <Typography sx={styles.emailIdText}>{`Enter code sent to ${
          state.email ? state.email : ""
        }`}</Typography>

        <Stack direction={"row"} alignItems={"center"} gap={2} mt={2}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              variant="outlined"
              size="small"
              slotProps={{
                input: {
                  style: {
                    fontSize: "20px",
                    backgroundColor: "rgba(15, 80, 81, 0.2)",
                  },
                },
              }}
              sx={{
                width: "45px",
                height: "45px",
                "& input": {
                  textAlign: "center", // Centering the text inside the input
                },
              }}
            />
          ))}
        </Stack>
        <Typography sx={styles.errorText}>{otpError}</Typography>
        {loading ? (
          <CircularProgress sx={{ marginTop: "1em" }} />
        ) : (
          <PrimaryButton
            title="Verify Email"
            onClick={handleVerifyOtp}
            viewStyle={{ marginTop: "1em", width: "100%" }}
          />
        )}

        <Button
          variant="text"
          disabled={isDisabled}
          sx={styles.resendOtpText}
          onClick={() => handleResendOTP()}
        >
          {isDisabled ? (
            <Typography
              sx={styles.resendDisabledOtpText}
            >{`Resend OTP in ${counter}s`}</Typography>
          ) : (
            <Typography sx={styles.resendOtpText}>Resend OTP</Typography>
          )}
        </Button>

        <Typography sx={styles.supportTextHelp}>
          Need help?{" "}
          <Link
            style={styles.supportLinkHelp}
            to="/contact-us"
            onClick={() => {
              resetDispatch();
            }}
          >
            contact customer care support
          </Link>
        </Typography>
      </Box>
    </motion.div>
  );
};
export default VerificationCodePage;
const styles = {
  subText: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  emailIdText: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.text.primary,
    fontWeight: 500,
    marginTop: "2em",
  },
  supportTextHelp: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.text.primary,
    fontWeight: 500,
    marginTop: "3em",
  },
  supportLinkHelp: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: "none",
  },
  errorText: {
    fontSize: responsiveFontSize(12),
    fontWidth: 400,
    color: "red",
    marginBottom: "3em",
  },
  resendOtpText: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.primary.main,
    textTransform: "none",
    fontWeight: 500,
    marginTop: "0.5em",
    textAlign: "right",
    "&:hover": {
      cursor: "pointer",
    },
  },
  resendDisabledOtpText: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.text.secondary,
    textTransform: "none",
    fontWeight: 500,
    marginTop: "0.5em",
    textAlign: "right",
  },
};
